import { focusStyle } from "@/components/profile/PhotoFocusEditor";
import { Link } from "react-router-dom";
import { Loader2, ImageOff } from "lucide-react";
import { STATUS, imageUrl } from "@/lib/api";
import { cn } from "@/lib/utils";

const TONES = {
  muted: "border-foreground/20 text-muted-foreground",
  emerald: "border-primary/50 text-foreground [&>i]:bg-primary",
  "emerald-solid": "border-primary bg-primary/10 text-primary [&>i]:bg-primary",
  gold: "border-[hsl(var(--gold))]/60 text-foreground [&>i]:bg-[hsl(var(--gold))]",
  red: "border-destructive/50 text-destructive [&>i]:bg-destructive",
};

export const StatusBadge = ({ status, className }) => {
  const s = STATUS[status] || STATUS.draft;
  return (
    <span data-testid={`status-badge-${status}`} className={cn("inline-flex h-8 items-center gap-2 whitespace-nowrap rounded-full border px-4 text-[13px]", TONES[s.tone], className)}>
      <i className="h-2 w-2 rounded-full bg-muted-foreground" aria-hidden />
      {s.label}
    </span>
  );
};

const SHARE_TONES = {
  draft: "bg-secondary text-muted-foreground",
  proposed: "bg-secondary text-foreground",
  accepted: "bg-primary/15 text-primary",
  declined: "bg-destructive/15 text-destructive",
};
const SHARE_LABELS = { draft: "Draft", proposed: "Proposed", accepted: "Accepted", declined: "Declined" };

export const ShareStatus = ({ status, testId }) => (
  <span data-testid={testId} className={cn("inline-flex h-8 items-center gap-2 rounded-md px-3 text-[13px]", SHARE_TONES[status])}>
    <i className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden />
    {SHARE_LABELS[status]}
  </span>
);

export const Avatar = ({ person, size = 44, className }) => {
  const src = imageUrl(person?.photo);
  const initials = (person?.name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
  return (
    <span className={cn("inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-background bg-secondary text-sm font-semibold text-foreground", className)} style={{ width: size, height: size }}>
      {src ? <img src={src} alt={person?.name || ""} className="img-cover" loading="lazy" style={person?.photo_focus?.path === person?.photo ? focusStyle(person.photo_focus) : undefined} /> : initials}
    </span>
  );
};

export const AvatarStack = ({ people = [], size = 40 }) => (
  <div className="flex -space-x-2">
    {people.slice(0, 4).map((p, i) => <Avatar key={i} person={p} size={size} />)}
  </div>
);

export const Spinner = ({ label = "Loading", className }) => (
  <div role="status" data-testid="loading-state" className={cn("flex items-center justify-center gap-3 py-24 text-muted-foreground", className)}>
    <Loader2 className="h-5 w-5 animate-spin text-primary" aria-hidden /> <span>{label}…</span>
  </div>
);

export const EmptyState = ({ title, body, action, testId = "empty-state" }) => (
  <div data-testid={testId} className="surface flex flex-col items-start gap-3 p-8 sm:p-10">
    <h3 className="font-display text-2xl sm:text-3xl">{title}</h3>
    {body && <p className="max-w-lg text-muted-foreground">{body}</p>}
    {action}
  </div>
);

export const ErrorState = ({ message, onRetry }) => (
  <div role="alert" data-testid="error-state" className="surface flex flex-col items-start gap-3 border-destructive/40 p-8">
    <ImageOff className="h-5 w-5 text-destructive" aria-hidden />
    <p>{message}</p>
    {onRetry && <button type="button" className="btn-outline" onClick={onRetry} data-testid="error-retry-button">Try again</button>}
  </div>
);

export const IllustrativeTag = ({ label = "Illustrative concept" }) => (
  <span data-testid="illustrative-tag" className="inline-flex h-7 items-center rounded-md border border-foreground/25 bg-background/70 px-3 text-xs text-foreground/80 backdrop-blur">
    {label}
  </span>
);

export const Crumbs = ({ items }) => (
  <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
    {items.map((it, i) => (
      <span key={i} className="flex items-center gap-2">
        {i > 0 && <span aria-hidden>/</span>}
        {it.to ? <Link to={it.to} className="hover:text-foreground">{it.label}</Link> : <span className="text-foreground/90">{it.label}</span>}
      </span>
    ))}
  </nav>
);
